import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Fab,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Mail as MailIcon,
  Send as SendIcon,
  Drafts as DraftsIcon,
  Star as StarIcon,
  Label as LabelIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../layout/DashboardLayout';
import { GmailInbox } from '../gmail/GmailInbox';
import { EmailCompose } from '../gmail/EmailCompose';
import { EmailDetail } from '../gmail/EmailDetail';
import { useGmail } from '../../hooks/useGmail';
import { useDatabase } from '../../hooks/useDatabase';
import { Contact } from '../../types/database';
import { GmailConnectionStatus, EmailMetadata, EmailDetailData } from '../../types/gmail';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`email-tabpanel-${index}`}
      aria-labelledby={`email-tab-${index}`}
      style={{ display: value === index ? 'block' : 'none' }}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const EmailPage: React.FC = () => {
  const { checkConnection, getEmail } = useGmail();
  const db = useDatabase();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [connectionStatus, setConnectionStatus] = useState<GmailConnectionStatus>({ isConnected: false });
  const [activeTab, setActiveTab] = useState(0);
  const [composeOpen, setComposeOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailMetadata | null>(null);
  const [emailDetailOpen, setEmailDetailOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [replyToEmail, setReplyToEmail] = useState<EmailMetadata | null>(null);
  const [forwardToEmail, setForwardToEmail] = useState<EmailMetadata | null>(null);
  const [forwardToEmailDetail, setForwardToEmailDetail] = useState<EmailDetailData | null>(null);

  // Check connection status on mount
  useEffect(() => {
    const checkStatus = async () => {
      const status = await checkConnection();
      setConnectionStatus(status);
    };
    checkStatus();
  }, [checkConnection]);

  const tabs = useMemo(() => [
    { label: 'Priority', icon: <LabelIcon />, query: 'is:important' },
    { label: 'All Emails', icon: <MailIcon />, query: 'in:inbox' },
    { label: 'Sent', icon: <SendIcon />, query: 'in:sent' },
    { label: 'Drafts', icon: <DraftsIcon />, query: 'in:drafts' },
    { label: 'Starred', icon: <StarIcon />, query: 'is:starred' },
  ], []);

  const loadEmails = useCallback(async (query?: string) => {
    if (!connectionStatus.isConnected) return;
    
    setRefreshing(true);
    try {
      // Email loading is now handled by GmailInbox component
      console.log('Loading emails for query:', query || tabs[activeTab].query);
    } catch (err) {
      console.error('Failed to load emails:', err);
    } finally {
      setRefreshing(false);
    }
  }, [connectionStatus.isConnected, activeTab, tabs]);

  useEffect(() => {
    loadEmails();
  }, [loadEmails]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleEmailClick = (email: EmailMetadata) => {
    setSelectedEmail(email);
    setEmailDetailOpen(true);
  };

  const handleComposeEmail = () => {
    setComposeOpen(true);
  };

  const handleEmailSent = () => {
    setComposeOpen(false);
    // Email refresh is handled by GmailInbox component
  };

  const handleReply = (email: EmailMetadata) => {
    setReplyToEmail(email);
    setForwardToEmail(null);
    setEmailDetailOpen(false);
    setComposeOpen(true);
  };

  const handleForward = async (email: EmailMetadata, emailDetail?: EmailDetailData | null) => {
    setForwardToEmail(email);
    setReplyToEmail(null);
    setEmailDetailOpen(false);
    
    // If emailDetail is not provided, fetch it
    if (!emailDetail) {
      try {
        const detail = await getEmail(email.id);
        setForwardToEmailDetail(detail);
      } catch (err) {
        console.error('Failed to fetch email detail for forwarding:', err);
        setForwardToEmailDetail(null);
      }
    } else {
      setForwardToEmailDetail(emailDetail);
    }
    
    setComposeOpen(true);
  };

  const handleAddContact = useCallback(async (email: string, name: string) => {
    try {
      // First check if contact already exists
      const existingContact = await db.getContactByEmail(email);
      if (existingContact.data) {
        // Contact already exists, no need to add
        return;
      }

      // Find contacts with similar names
      const findSimilarContacts = (contactName: string, allContacts: Contact[]) => {
        const normalizedName = contactName.toLowerCase().trim();
        const nameWords = normalizedName.split(' ').filter(word => word.length > 2);
        
        return allContacts.filter(contact => {
          const contactName = contact.name.toLowerCase().trim();
          const contactWords = contactName.split(' ').filter(word => word.length > 2);
          
          // Check for exact match
          if (contactName === normalizedName) return true;
          
          // Check for partial word matches
          const matchingWords = nameWords.filter(word => 
            contactWords.some(contactWord => 
              contactWord.includes(word) || word.includes(contactWord)
            )
          );
          
          // Consider similar if at least 50% of words match
          return matchingWords.length >= Math.ceil(nameWords.length / 2);
        });
      };

      // Get all contacts to check for similar names
      const allContactsResult = await db.getContacts();
      if (allContactsResult.data) {
        const similarContacts = findSimilarContacts(name, allContactsResult.data);
        
        if (similarContacts.length > 0) {
          // For now, just create the contact anyway
          // In the future, could show a dialog like GmailInbox does
          console.log('Similar contacts found:', similarContacts);
        }
      }

      // Create new contact
      const newContact = await db.createContact({
        name,
        email,
        tags: {},
        associated_contacts: [],
        relationship_types: {},
        user_id: '', // This will be set by the database function
      });

      if (newContact.data) {
        // Trigger networking suggestions recalculation
        setTimeout(async () => {
          try {
            await db.recalculateNetworkingSuggestions();
          } catch (err) {
            console.error('Error triggering networking suggestions recalculation:', err);
          }
        }, 2000);
      } else {
        console.error('Failed to add contact:', newContact.error);
      }
    } catch (err) {
      console.error('Error adding contact:', err);
    }
  }, [db]);

  const handleEmailDetailClose = () => {
    setEmailDetailOpen(false);
    setSelectedEmail(null);
  };

  const handleSyncToEvent = (emailId: string, contactId?: string) => {
    // Handle syncing email to event
    console.log('Syncing email to event:', emailId, contactId);
  };


  const handleRefresh = () => {
    // Refresh is handled by GmailInbox component
    console.log('Refreshing emails');
  };

  if (!connectionStatus.isConnected) {
    return (
      <DashboardLayout title="Email">
        <Box sx={{ p: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Gmail is not connected. Please connect your Gmail account in Settings to use email features.
          </Alert>
          <Button 
            variant="contained" 
            onClick={() => window.location.href = '/settings'}
            startIcon={<MailIcon />}
          >
            Connect Gmail
          </Button>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Email">
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Combined Header and Main Content */}
        <Paper sx={{ 
          p: isMobile ? 1 : 2, 
          mb: 2, 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          boxShadow: 'none', 
          border: 'none',
          minHeight: 0 // Allow flex child to shrink
        }}>
          {/* Header */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center', 
            mb: 2,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0
          }}>
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                fontSize: isMobile ? '1.1rem' : undefined
              }}
            >
              <MailIcon />
              Email - {connectionStatus.emailAddress || 'Gmail'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton onClick={handleRefresh} disabled={refreshing} size={isMobile ? "small" : "medium"}>
                {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </Box>
          </Box>

          {/* Tabs - Dropdown on mobile, tabs on desktop */}
          {isMobile ? (
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Email Filter</InputLabel>
                <Select
                  value={activeTab}
                  onChange={(e) => setActiveTab(Number(e.target.value))}
                  label="Email Filter"
                  sx={{ textAlign: 'left' }}
                >
                  {tabs.map((tab, index) => (
                    <MenuItem key={index} value={index}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {tab.icon}
                        {tab.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ) : (
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons={false}
              sx={{ mb: 2, '& .MuiTabs-indicator': { display: 'none' } }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  icon={tab.icon}
                  label={tab.label}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          )}

          {/* Main Content */}
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {tabs.map((tab, index) => (
              <TabPanel key={index} value={activeTab} index={index}>
                <GmailInbox 
                  onEmailClick={handleEmailClick}
                  query={tab.query}
                />
              </TabPanel>
            ))}
          </Box>
        </Paper>

        {/* Floating Action Button */}
        <Fab
          color="primary"
          aria-label="compose email"
          onClick={handleComposeEmail}
          size={isMobile ? "medium" : "large"}
          sx={{
            position: 'fixed',
            bottom: isMobile ? 16 : 24,
            right: isMobile ? 16 : 24,
          }}
        >
          <AddIcon />
        </Fab>

        {/* Email Compose Modal */}
        <EmailCompose
          open={composeOpen}
          onClose={() => {
            setComposeOpen(false);
            setReplyToEmail(null);
            setForwardToEmail(null);
            setForwardToEmailDetail(null);
          }}
          onSent={handleEmailSent}
          replyTo={replyToEmail || undefined}
          forwardTo={forwardToEmail || undefined}
          forwardToDetail={forwardToEmailDetail || undefined}
        />

        {/* Email Detail Modal */}
        {selectedEmail && (
          <EmailDetail
            open={emailDetailOpen}
            onClose={handleEmailDetailClose}
            email={selectedEmail}
            onReply={handleReply}
            onForward={handleForward}
            onAddContact={handleAddContact}
            onSyncToEvent={handleSyncToEvent}
          />
        )}
      </Box>
    </DashboardLayout>
  );
};